'use client';

import React, { FC } from 'react';
import { Avatar } from 'app/(authenticated)/(member)/_components/Avatar';
import { CheckCircleIcon } from 'components/icons/v2';
import { SiGithub, SiInstagram, SiLinkedin, SiX } from '@icons-pack/react-simple-icons';
import { DreamNode } from 'generated/types';
import { StaticImageData } from 'next/image';

interface HeroCardProps {
  user: {
    firstName: string;
    lastName: string;
    image?: StaticImageData | null;
    city?: string | null | { name?: string };
    about?: string;
    tags?: Array<{ name: string; isHobby?: boolean }>;
    dreams?: DreamNode[];
    linkTwitter?: string | null;
    linkInstagram?: string | null;
    linkLinkedin?: string | null;
    linkGithub?: string | null;
  };
}

export const LandingHeroCard: FC<HeroCardProps> = ({ user }) => {
  // Extract city name if it's an object
  const cityName = typeof user.city === 'string' ? user.city : user.city?.name;

  // Limit tags to 5-6 for the card
  const displayTags = user.tags?.slice(0, 6) || [];

  return (
    <div className="bg-white rounded-2xl shadow-[0_0_45px_rgba(0,0,0,0.14)] p-6 md:p-4 w-[441px] md:w-full md:max-w-sm h-[505px] md:h-auto flex flex-col gap-4 md:gap-3">
      {/* Avatar, Name, Location section */}
      <div className="flex flex-col gap-2 md:gap-1 items-start">
        <div className="rounded-full overflow-hidden border-[3px] border-white">
          <Avatar
            avatar={user.image}
            alt={`${user.firstName} ${user.lastName}`}
            height={65}
            width={65}
            user={{
              firstName: user.firstName,
              lastName: user.lastName,
            }}
            containerClassname="text-display-md text-gray-500 font-semibold md:text-lg"
            defaultIconHeight={55}
            defaultIconWidth={55}
          />
        </div>

        <div className="flex flex-col gap-1 md:gap-0.5 flex-1">
          <h2 className="text-xl md:text-lg text-gray-800 font-semibold">
            {user.firstName} {user.lastName}
          </h2>
          {cityName && (
            <p className="text-small md:text-xs text-gray-500 font-medium">{cityName}</p>
          )}
        </div>

        {/* Social Icons section */}
        {(user.linkTwitter || user.linkInstagram || user.linkLinkedin || user.linkGithub) && (
          <div className="flex gap-[15px] md:gap-3 text-gray-400 py-1">
            {user.linkTwitter && (
              <SiX width={18} height={14} />
            )}
            {user.linkInstagram && (
              <SiInstagram width={18} height={14} />
            )}
            {user.linkLinkedin && (
              <SiLinkedin width={18} height={14} />
            )}
            {user.linkGithub && (
              <SiGithub width={18} height={14} />
            )}
          </div>
        )}
      </div>


      {/* About section */}
      {user.about && (
        <p className="text-small md:text-small text-gray-800 font-medium line-clamp-3 md:line-clamp-2 whitespace-pre-line">
          {user.about}
        </p>
      )}

      {/* Tags section */}
      {displayTags.length > 0 && (
        <div className="flex flex-col gap-2 md:gap-1">
          <div className="flex gap-1 flex-wrap text-gray-500 text-small md:text-xs">
            <span className="font-medium">Ask me about:</span>

            {displayTags.map((tag, index) => {
              const isHobby = tag.isHobby ? 'text-brand-400' : 'text-gray-500';
              return (
                <span key={index} className={`${isHobby} underline font-medium`}>
                  {tag.name}
                  {index < displayTags.length - 1 ? ',' : ''}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Dreams section */}
      {user.dreams && user.dreams.length > 0 && (
        <div className="flex flex-col gap-3 md:gap-2">
          <h3 className="text-small md:text-xs font-semibold text-gray-800">Dreams</h3>
          <div className="flex gap-4 md:flex-col md:gap-2">
            {/* Left column (or single column on mobile) */}
            <div className="flex flex-col gap-3 md:gap-2 flex-1">
              {user.dreams.slice(0, Math.ceil(user.dreams.length / 2)).map((dream, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon
                      className={dream.achieved ? 'text-brand-500' : 'text-gray-400'}
                    />
                  </div>
                  <span className={`text-small md:text-xs ${dream.achieved ? 'text-gray-800' : 'text-gray-500'} font-medium`}>
                    {dream.title}
                  </span>
                </div>
              ))}
            </div>
            {/* Right column (hidden on mobile) */}
            {user.dreams.length > 1 && (
              <div className="flex flex-col gap-3 md:gap-2 flex-1 md:hidden">
                {user.dreams.slice(Math.ceil(user.dreams.length / 2)).map((dream, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon
                        className={dream.achieved ? 'text-brand-500' : 'text-gray-400'}
                      />
                    </div>
                    <span className={`text-small md:text-xs ${dream.achieved ? 'text-gray-800' : 'text-gray-500'} font-medium`}>
                      {dream.title}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
